import { computed, inject } from 'vue';
import { useRoute } from 'vue-router';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import useDriveBooleans from '@/composables/UseDriveBooleans';
import { NAVIGATION_PAGES, SHARE_SETTINGS } from '@/types/enums';

export default function useHeaderBooleans() {
  // eslint-disable-next-line @typescript-eslint/ban-types
  const $localize = inject('$localize') as Function;

  const route = useRoute();
  const driveStore = useDriveStore();
  const {
    currentPage, isNetwork, isCorporate, isNetworkCaseView, isCaseView,
  } = useDriveBooleans();

  /** Boolean used in inverted mode - false will be inverted to true to show search */
  const hideHeaderSearch = computed(() => route.meta.hideHeaderSearch);

  /** Provides custom title to header */
  const customTitle = computed(() => {
    if (route.meta.customTitle) {
      if (route.meta.navigationPage === NAVIGATION_PAGES.DRIVE_FOLDER) {
        return driveStore.currentFolder?.name || 'No info';
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.DRIVE) {
        return isCorporate.value ? $localize('drive.header.corporate.title') : $localize('drive.header.title');
      }
      return driveStore.currentCase.title;
    }
    return '';
  });

  /** ??? */
  const isSelector = computed(
    () => {
      if (isNetwork.value) {
        return false;
      }
      if (currentPage.value === NAVIGATION_PAGES.DRIVE_PHOTOS) {
        return !!driveStore.photos.data.length;
      }
      if (currentPage.value === NAVIGATION_PAGES.DRIVE_CASES) {
        return !!driveStore.cases.data.length;
      }
      if (currentPage.value === NAVIGATION_PAGES.DRIVE_FILES) {
        return !!driveStore.mixedData.data.length;
      }
      if (currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER) {
        return !!driveStore.currentFolder?.mixedData?.length;
      }
      return false;
    },
  );

  /** Boolean value that hides back arrow in header, based on route.meta value, default is false */
  const isBackArrow = computed(() => route.meta?.backArrow || false);

  /** ??? */
  const topRightText = computed(() => {
    if (NAVIGATION_PAGES.CASE_CREATE === currentPage.value) {
      return $localize('drive.create.case.next.button');
    }
    return '';
  });

  /** ??? */
  const isDoneDisabled = computed(() => {
    const isPage = currentPage.value === NAVIGATION_PAGES.CASE_CREATE;
    const isNotValid = driveStore.caseTitleValidation || driveStore.caseDescriptionValidation
      || driveStore.casePatientNameValidation || driveStore.casePatientIdValidation || !driveStore.bufferCurrentCaseTitle;

    return isPage && isNotValid;
  });

  /**
   * Computed property to determine action bar settings.
   * - `showBar` and `showDots` are shown for my case or network case view.
   * - `showShare` and `showSave` are shown for my case, or if the current case allows resharing or saving.
   */
  const actionBar = computed(() => { // header
    const caseShareOrSave = isCaseView.value && !isNetwork.value;
    const networkCaseShare = (driveStore.currentCase?.root && driveStore.currentCase?.shareSettings?.includes(SHARE_SETTINGS.RESHARE_ALLOWED));
    const networkCaseSave = (driveStore.currentCase?.root && driveStore.currentCase?.shareSettings?.includes(SHARE_SETTINGS.ALLOW_SAVE));
    return {
      showBar: isCaseView.value || isNetworkCaseView.value,
      showDots: isCaseView.value || isNetworkCaseView.value,
      showShare: caseShareOrSave || networkCaseShare,
      showSave: caseShareOrSave || networkCaseSave,
    };
  });
  return {
    hideHeaderSearch,
    customTitle,
    isSelector,
    isBackArrow,

    topRightText,
    actionBar,
    isDoneDisabled,
  };
}
